import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { FiSearch, FiX, FiPlus } from "react-icons/fi";
import { useQuery } from "react-query";
import { ingredientsAPI } from "../services/api";

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  transition: border-color 0.3s ease;

  &:focus-within {
    border-color: #2d5016;
  }

  input {
    flex: 1;
    padding: 0.75rem;
    border: none;
    outline: none;
    font-size: 0.875rem;
    background: transparent;
  }

  .search-icon {
    padding: 0 0.75rem;
    color: #666;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 4px;
`;

const DropdownItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: #f5f5f5;
  }

  .ingredient-name {
    font-weight: 500;
    color: #333;
  }

  .ingredient-category {
    font-size: 0.75rem;
    color: #666;
    text-transform: capitalize;
  }
`;

const SelectedIngredients = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const IngredientChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #e6f2e6;
  border: 1px solid #2d5016;
  border-radius: 20px;
  font-size: 0.875rem;
  color: #2d5016;

  .remove-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #2d5016;
    color: white;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: #1a3a0e;
    }
  }
`;

const EmptyState = styled.div`
  padding: 1rem;
  text-align: center;
  color: #666;
  font-size: 0.875rem;
`;

const LoadingState = styled.div`
  padding: 1rem;
  text-align: center;
  color: #666;
  font-size: 0.875rem;
`;

const CreateIngredientOption = styled(DropdownItem)`
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;

  &:hover {
    background: #e6f2e6;
  }

  .create-text {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #2d5016;
    font-weight: 500;
  }
`;

const IngredientSearch = ({
  selectedIngredients = [],
  onIngredientsChange,
  placeholder = "Search ingredients...",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch ingredients
  const { data: ingredientsData, isLoading } = useQuery(
    ["ingredients", debouncedSearchTerm],
    () =>
      ingredientsAPI.searchIngredients({
        search: debouncedSearchTerm || undefined,
        limit: 20,
      }),
    {
      enabled: debouncedSearchTerm.length >= 1 || debouncedSearchTerm === "",
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const ingredients = ingredientsData?.data?.data?.ingredients || [];
  const selectedIngredientIds = new Set(
    selectedIngredients.map((ing) => (typeof ing === "object" ? ing.id : ing))
  );

  // Filter out already selected ingredients
  const availableIngredients = ingredients.filter(
    (ing) => !selectedIngredientIds.has(ing.id)
  );

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectIngredient = (ingredient) => {
    const newIngredients = [
      ...selectedIngredients,
      { id: ingredient.id, name: ingredient.name, category: ingredient.category },
    ];
    onIngredientsChange(newIngredients);
    setSearchTerm("");
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const handleRemoveIngredient = (ingredientId) => {
    const newIngredients = selectedIngredients.filter(
      (ing) => (typeof ing === "object" ? ing.id : ing) !== ingredientId
    );
    onIngredientsChange(newIngredients);
  };

  const handleCreateIngredient = async () => {
    if (!searchTerm.trim()) return;

    try {
      const response = await ingredientsAPI.createIngredient({
        name: searchTerm.trim(),
      });
      if (response.data.success) {
        handleSelectIngredient(response.data.data.ingredient);
      }
    } catch (error) {
      console.error("Error creating ingredient:", error);
      // If ingredient already exists, try to find it
      if (error.response?.status === 409) {
        // Ingredient already exists, find it in the list
        const existingIngredient = ingredients.find(
          (ing) => ing.name.toLowerCase() === searchTerm.toLowerCase()
        );
        if (existingIngredient) {
          handleSelectIngredient(existingIngredient);
        }
      }
    }
  };

  const showCreateOption =
    searchTerm.trim() &&
    !availableIngredients.some(
      (ing) => ing.name.toLowerCase() === searchTerm.toLowerCase()
    ) &&
    !selectedIngredientIds.has(searchTerm.toLowerCase());

  return (
    <SearchContainer ref={containerRef}>
      <SearchInputWrapper>
        <FiSearch className="search-icon" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
        />
      </SearchInputWrapper>

      {showDropdown && (availableIngredients.length > 0 || showCreateOption) && (
        <Dropdown>
          {isLoading ? (
            <LoadingState>Searching...</LoadingState>
          ) : availableIngredients.length > 0 ? (
            <>
              {availableIngredients.map((ingredient) => (
                <DropdownItem
                  key={ingredient.id}
                  onClick={() => handleSelectIngredient(ingredient)}
                >
                  <span className="ingredient-name">{ingredient.name}</span>
                  {ingredient.category && (
                    <span className="ingredient-category">
                      {ingredient.category}
                    </span>
                  )}
                </DropdownItem>
              ))}
              {showCreateOption && (
                <CreateIngredientOption onClick={handleCreateIngredient}>
                  <span className="create-text">
                    <FiPlus />
                    Create "{searchTerm}"
                  </span>
                </CreateIngredientOption>
              )}
            </>
          ) : showCreateOption ? (
            <CreateIngredientOption onClick={handleCreateIngredient}>
              <span className="create-text">
                <FiPlus />
                Create "{searchTerm}"
              </span>
            </CreateIngredientOption>
          ) : (
            <EmptyState>No ingredients found</EmptyState>
          )}
        </Dropdown>
      )}

      {selectedIngredients.length > 0 && (
        <SelectedIngredients>
          {selectedIngredients.map((ingredient) => {
            const id = typeof ingredient === "object" ? ingredient.id : ingredient;
            const name = typeof ingredient === "object" ? ingredient.name : ingredient;
            return (
              <IngredientChip key={id}>
                <span>{name}</span>
                <button
                  className="remove-btn"
                  onClick={() => handleRemoveIngredient(id)}
                  type="button"
                >
                  <FiX size={12} />
                </button>
              </IngredientChip>
            );
          })}
        </SelectedIngredients>
      )}
    </SearchContainer>
  );
};

export default IngredientSearch;

